import type { NavigateFunction, NavigateOptions } from "react-router-dom";
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import type DocumentReference from "@/models/conversation/document";
import {
  DocumentIndexType,
  normalizeDocumentIndex,
} from "@/models/conversation/document";
import AppRoutes, {
  type ReferenceHighlightRouteOptions,
} from "@/constants/routes/AppRoutes";
import {
  isOutgoingReferenceFromCurrentDoc,
  parseLinkExtraMetadata,
} from "@/utils/documentLinkOtherSide";
import { applyDocumentNavigationStackOnNavigate } from "@/lib/navigation/documentNavigationStackNavigation";
import {
  appendConsolidationParamsToPath,
  consolidationNavigationParams,
  resolveConsolidationLinkNavigation,
  type ConsolidationNavigationOptions,
} from "@/lib/consolidationMetadata";
import { DATASOURCE_CONFIG, normalizeDataSource } from "@/config/datasources";

export type NavigationContext = "chat" | "app";

export type AppNavigationTarget =
  | {
      kind: "legislation";
      documentId: string;
      source: string;
      htmlIds?: string[];
      refHighlight?: ReferenceHighlightRouteOptions;
      context: NavigationContext;
      /** Reopens a consolidated render instead of the plain LBK/LOVH text. */
      consolidation?: ConsolidationNavigationOptions;
      /** Seeds the viewer's in-document search (global search result open). */
      docSearch?: string;
    }
  | {
      kind: "legislationPdf";
      documentId: string;
      source?: string;
      context: NavigationContext;
    }
  | {
      kind: "workspaceDocument";
      workspaceId: string;
      documentId: string;
      htmlIds?: string[];
      refHighlight?: ReferenceHighlightRouteOptions;
      /** Seeds the viewer's in-document search (global search result open). */
      docSearch?: string;
    }
  | {
      kind: "workspaceFiles";
      workspaceId: string;
      /** Seeds the files tab's search field (the Workspaces page's "Åbn workspace"). */
      search?: string;
    }
  | {
      kind: "workspaceFilesFolder";
      workspaceId: string;
      folderPath: string;
    }
  | {
      kind: "workspaceComments";
      workspaceId: string;
      documentId?: string;
      scrollToCommentId?: string;
    }
  | {
      kind: "chatConversation";
    }
  | {
      kind: "documentLink";
      link: DocumentLink;
      currentDocumentId?: string;
      context: NavigationContext;
    }
  | {
      kind: "documentReference";
      reference: DocumentReference;
    }
  | {
      kind: "path";
      path: string;
    };

/** The address parameter that hands a search to a workspace's files tab (LAR-235). */
export const WORKSPACE_FILES_SEARCH_PARAM = "search";

export type BuildAppNavigationUrlOptions = {
  pathname?: string;
};

export type OpenAppNavigationOptions = {
  target: AppNavigationTarget;
  navigate: NavigateFunction;
  event?: React.MouseEvent | MouseEvent;
  replace?: boolean;
  navigateOptions?: NavigateOptions;
  onBeforeSameTabNavigate?: () => void;
  /** Called before same-tab navigation to a workspace document (not new-tab opens). */
  onWorkspaceDocumentNavigate?: () => void;
  /** push = cross-document hop; replace = timeline / same-depth swap */
  navigationMode?: "push" | "replace";
};

/**
 * Returns the modifier key label based on the user's OS.
 */
export const getModifierKeyLabel = (): "⌘" | "Ctrl" => {
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  return isMac ? "⌘" : "Ctrl";
};

export const NEW_TAB_MODIFIER_TOOLTIP = `${getModifierKeyLabel()} + klik for at åbne i ny fane`;

/**
 * Checks if the modifier key (CMD on Mac, CTRL on Windows/Linux) is pressed.
 */
export const isModifierKeyPressed = (
  event: React.MouseEvent | MouseEvent
): boolean => {
  return event.metaKey || event.ctrlKey;
};

export const shouldOpenInNewTab = (
  event?: React.MouseEvent | MouseEvent
): boolean => {
  if (!event) {
    return false;
  }
  return isModifierKeyPressed(event) || event.button === 1;
};

export const legislationTarget = (
  documentId: string,
  source: string,
  context: NavigationContext = "app",
  options?: {
    htmlIds?: string[];
    refHighlight?: ReferenceHighlightRouteOptions;
  }
): AppNavigationTarget => ({
  kind: "legislation",
  documentId,
  source,
  context,
  htmlIds: options?.htmlIds,
  refHighlight: options?.refHighlight,
});

export const workspaceDocumentTarget = (
  workspaceId: string,
  documentId: string,
  options?: {
    htmlIds?: string[];
    refHighlight?: ReferenceHighlightRouteOptions;
  }
): AppNavigationTarget => ({
  kind: "workspaceDocument",
  workspaceId,
  documentId,
  htmlIds: options?.htmlIds,
  refHighlight: options?.refHighlight,
});

/**
 * A chat citation can only be opened when its documentId is a real id.
 * Chunks indexed without one carry the `N/A` placeholder.
 */
export const isValidReferenceDocumentId = (
  documentId: string | null | undefined
): boolean => {
  if (!documentId) {
    return false;
  }
  const trimmed = documentId.trim();
  return trimmed.length > 0 && trimmed.toLowerCase() !== "n/a";
};

/**
 * Whether the web app can open a chat citation at all.
 *
 * Legislation (Kilder) citations are routed as `/chat/legislation/:documentId`,
 * so a slash in the id would split the route and land on the 404 page.
 * Twin of `DocumentReference.is_navigable` in
 * services/chat-api/src/shared/core/document/document_reference.py; chat-api
 * drops such citations at generation time, this re-checks persisted
 * conversations. Keep the two rules identical.
 */
export const isNavigableReference = (
  reference: Pick<DocumentReference, "documentId" | "documentIndex">
): boolean => {
  if (!isValidReferenceDocumentId(reference.documentId)) {
    return false;
  }
  const documentIndex = normalizeDocumentIndex(reference.documentIndex ?? "");
  if (
    documentIndex === DocumentIndexType.Retsinformation &&
    reference.documentId.includes("/")
  ) {
    return false;
  }
  return true;
};

export const hasHtmlAnchors = (
  reference: Pick<DocumentReference, "htmlIds">
): boolean => Boolean(reference.htmlIds && reference.htmlIds.length > 0);

export type LegislationReferenceView = "html" | "pdf";

/**
 * Which chat viewer a legislation (Kilder) citation opens in.
 *
 * Anchors win, then legacy bounding boxes keep the PDF viewer, and a source that
 * only exists as HTML (Arvid, Domsdatabasen, …) falls back to the HTML viewer
 * instead of a PDF blob that was never uploaded.
 */
export const resolveLegislationReferenceView = (
  reference: Pick<DocumentReference, "htmlIds" | "boundingBoxes" | "source">
): LegislationReferenceView => {
  if (hasHtmlAnchors(reference)) {
    return "html";
  }
  if (reference.boundingBoxes && reference.boundingBoxes.length > 0) {
    return "pdf";
  }
  const dataSource = normalizeDataSource(reference.source);
  if (dataSource && DATASOURCE_CONFIG[dataSource].supportsHtmlView) {
    return "html";
  }
  return "pdf";
};

const resolveNavigationContext = (
  context: NavigationContext | undefined,
  pathname?: string
): NavigationContext => {
  if (context) {
    return context;
  }
  if (pathname?.includes("/chat/")) {
    return "chat";
  }
  return "app";
};

export const buildDocumentLinkTargetFields = (
  link: DocumentLink,
  currentDocumentId: string | undefined,
  context: NavigationContext
): AppNavigationTarget | null => {
  const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, currentDocumentId);

  if (isOutgoing) {
    if (link.destinationWorkspaceId) {
      return {
        kind: "workspaceDocument",
        workspaceId: link.destinationWorkspaceId,
        documentId: link.destinationDocumentId,
        htmlIds: link.destinationHtmlIds,
        refHighlight: {
          matchText: link.destinationUniqueMatchText,
          chunkText: link.destinationUniqueChunkText,
        },
      };
    }
    if (link.destinationSource) {
      const consolidation = resolveConsolidationLinkNavigation(
        parseLinkExtraMetadata(link.extraMetadata)
      );
      return {
        kind: "legislation",
        documentId: link.destinationDocumentId,
        source: link.destinationSource,
        htmlIds: link.destinationHtmlIds,
        refHighlight: {
          matchText: link.destinationUniqueMatchText,
          chunkText: link.destinationUniqueChunkText,
        },
        context,
        ...(consolidation ? { consolidation } : {}),
      };
    }
    return null;
  }

  if (link.originWorkspaceId) {
    return {
      kind: "workspaceDocument",
      workspaceId: link.originWorkspaceId,
      documentId: link.originDocumentId ?? "",
      htmlIds: link.originHtmlIds,
      refHighlight: {
        matchText: link.originUniqueMatchText,
        chunkText: link.originUniqueChunkText,
      },
    };
  }
  if (link.originSource) {
    return {
      kind: "legislation",
      documentId: link.originDocumentId ?? "",
      source: link.originSource,
      htmlIds: link.originHtmlIds,
      refHighlight: {
        matchText: link.originUniqueMatchText,
        chunkText: link.originUniqueChunkText,
      },
      context,
    };
  }
  return null;
};

export const buildAppNavigationUrl = (
  target: AppNavigationTarget,
  options?: BuildAppNavigationUrlOptions
): string | null => {
  const pathname = options?.pathname;

  switch (target.kind) {
    case "path":
      return target.path;

    case "chatConversation":
      return AppRoutes.chat;

    case "workspaceFiles": {
      const path = AppRoutes.workspaceFiles(target.workspaceId);
      const search = target.search?.trim();
      return search
        ? `${path}?${WORKSPACE_FILES_SEARCH_PARAM}=${encodeURIComponent(search)}`
        : path;
    }

    case "workspaceFilesFolder":
      return AppRoutes.workspaceFilesFolder(
        target.workspaceId,
        target.folderPath
      );

    case "workspaceComments": {
      const base = target.documentId
        ? AppRoutes.workspaceCommentsDocument(
            target.workspaceId,
            target.documentId
          )
        : AppRoutes.workspaceComments(target.workspaceId);
      if (!target.scrollToCommentId) {
        return base;
      }
      const separator = base.includes("?") ? "&" : "?";
      return `${base}${separator}scrollToComment=${encodeURIComponent(target.scrollToCommentId)}`;
    }

    case "workspaceDocument":
      return AppRoutes.workspaceDocumentRedirect(
        target.workspaceId,
        target.documentId,
        target.htmlIds,
        { ...target.refHighlight, docSearch: target.docSearch }
      );

    case "legislation": {
      const context = resolveNavigationContext(target.context, pathname);
      const viewerOptions = {
        ...target.refHighlight,
        docSearch: target.docSearch,
      };
      const path =
        context === "chat"
          ? AppRoutes.chatLegislationHTMLView(
              target.documentId,
              target.source,
              target.htmlIds,
              viewerOptions
            )
          : AppRoutes.legislationHTML(
              target.documentId,
              target.source,
              target.htmlIds,
              viewerOptions
            );
      if (!target.consolidation) {
        return path;
      }
      return appendConsolidationParamsToPath(
        path,
        consolidationNavigationParams(target.consolidation)
      );
    }

    case "legislationPdf": {
      const context = resolveNavigationContext(target.context, pathname);
      if (context === "chat") {
        return AppRoutes.chatLegislationPDFView(target.documentId);
      }
      if (!target.source) {
        return null;
      }
      return AppRoutes.legislationPDF(target.documentId, target.source);
    }

    case "documentLink": {
      const context = resolveNavigationContext(target.context, pathname);
      const resolved = buildDocumentLinkTargetFields(
        target.link,
        target.currentDocumentId,
        context
      );
      return resolved ? buildAppNavigationUrl(resolved, options) : null;
    }

    case "documentReference": {
      const reference = target.reference;
      const documentIndexForSwitch = normalizeDocumentIndex(
        reference.documentIndex ?? ""
      );

      if (!documentIndexForSwitch || !isNavigableReference(reference)) {
        return null;
      }

      switch (documentIndexForSwitch) {
        case DocumentIndexType.Retsinformation:
          if (resolveLegislationReferenceView(reference) === "html") {
            return AppRoutes.chatLegislationHTMLView(
              reference.documentId,
              reference.source,
              hasHtmlAnchors(reference) ? reference.htmlIds : undefined
            );
          }
          return AppRoutes.chatLegislationPDFView(reference.documentId);

        case DocumentIndexType.Workspace:
          if (!reference.workspaceId) {
            return null;
          }
          if (reference.htmlIds && reference.htmlIds.length > 0) {
            return AppRoutes.chatWorkspaceHTMLView(
              reference.workspaceId,
              reference.documentId,
              reference.htmlIds
            );
          }
          return AppRoutes.chatWorkspacePDFView(
            reference.workspaceId,
            reference.documentId,
            reference.documentTitle.includes(".xls").toString()
          );

        case DocumentIndexType.Document:
          return null;

        default:
          return null;
      }
    }

    default:
      return null;
  }
};

const targetUsesWorkspaceDocument = (target: AppNavigationTarget): boolean => {
  if (target.kind === "workspaceDocument") {
    return true;
  }
  if (target.kind === "documentLink") {
    const resolved = buildDocumentLinkTargetFields(
      target.link,
      target.currentDocumentId,
      target.context
    );
    return resolved?.kind === "workspaceDocument";
  }
  if (target.kind === "documentReference") {
    const documentIndex = normalizeDocumentIndex(
      target.reference.documentIndex ?? ""
    );
    return (
      documentIndex === DocumentIndexType.Workspace &&
      Boolean(target.reference.workspaceId)
    );
  }
  return false;
};

/**
 * Opens an in-app destination in the same tab (default) or a new browser tab
 * (⌘/Ctrl+click or middle-click).
 */
export const openAppNavigation = ({
  target,
  navigate,
  event,
  replace = false,
  navigateOptions,
  onBeforeSameTabNavigate,
  onWorkspaceDocumentNavigate,
  navigationMode = "push",
}: OpenAppNavigationOptions): boolean => {
  const url = buildAppNavigationUrl(target);

  if (!url) {
    return false;
  }

  const openingNewTab = shouldOpenInNewTab(event);

  if (targetUsesWorkspaceDocument(target) && !openingNewTab) {
    onWorkspaceDocumentNavigate?.();
  }

  if (openingNewTab) {
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  }

  applyDocumentNavigationStackOnNavigate({
    target,
    replace,
    openingNewTab,
    navigationMode,
  });

  onBeforeSameTabNavigate?.();
  navigate(url, { replace, ...navigateOptions });
  return true;
};
